import type { Active, DragEndEvent, Over } from '@dnd-kit/core';
import {
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import type { LevelGroup } from '@/components/common/settings/item-types/lib/types';
import type { ItemType, ItemTypeLevel } from '@/lib/api/types/itemType';

export const transformItemsToLevelGroups = (
  itemTypes: ItemType[],
  itemLevels: ItemTypeLevel[],
): LevelGroup[] => {
  const groupedByLevel = itemTypes.reduce<Record<number, ItemType[]>>((groupedItemTypes, item) => {
    if (!(item.itemLevelId in groupedItemTypes)) {
      groupedItemTypes[item.itemLevelId] = [];
    }
    groupedItemTypes[item.itemLevelId]?.push(item);
    return groupedItemTypes;
  }, {});

  return itemLevels
    .map((levelData) => ({
      levelId: levelData.id,
      level: levelData.level,
      levelName: levelData.name,
      levelData: levelData,
      items: groupedByLevel[levelData.id] ?? [],
    }))
    .sort((a, b) => a.level - b.level);
};

export const createDragSensors = () =>
  useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {}),
  );

export interface RowReorderCollisionContext {
  sortedLevels: { id: number; level: number }[];
  lockedSpan: LockedSpan | null;
  levelByDroppableId: Map<string, number>;
}

export const createCollisionDetection = (
  activeDragType: 'row' | 'item' | null,
  rowReorder?: RowReorderCollisionContext,
) => {
  return (args: Parameters<typeof closestCenter>[0]) => {
    if (activeDragType === 'item') {
      const pointerCollisions = pointerWithin(args);
      const filteredCollisions = pointerCollisions.filter(
        (collision) => collision.data?.droppableContainer?.data?.current?.type === 'levelGroup',
      );
      return filteredCollisions.length > 0 ? filteredCollisions : rectIntersection(args);
    }
    const centerCollisions = closestCenter(args).filter(
      (collision) =>
        !collision.data?.droppableContainer?.data?.current?.type ||
        collision.data.droppableContainer?.data?.current?.type !== 'levelGroup',
    );

    // While a locked span exists, snap the drop indicator to the nearest collision whose resulting
    // reorder is valid, so the user can't rest the row inside the span (and then get an error).
    if (!rowReorder?.lockedSpan || centerCollisions.length === 0) {
      return centerCollisions;
    }

    const activeLevelId = Number(args.active.id);
    const nearestValid = centerCollisions.find((collision) => {
      const targetLevel = rowReorder.levelByDroppableId.get(String(collision.id));
      return (
        targetLevel !== undefined &&
        !wouldReorderSplitLockedSpan(
          rowReorder.sortedLevels,
          activeLevelId,
          targetLevel,
          rowReorder.lockedSpan,
        )
      );
    });

    return nearestValid ? [nearestValid] : centerCollisions;
  };
};

export const getDragModifiers = (activeDragType: 'row' | 'item' | null) =>
  activeDragType === 'row'
    ? [restrictToVerticalAxis, restrictToWindowEdges]
    : [restrictToWindowEdges];

export const handleRowReorder = (levelGroups: LevelGroup[], activeId: string, overId: string) => {
  const activeIndex = levelGroups.findIndex((group) => group.levelId.toString() === activeId);
  const overIndex = levelGroups.findIndex((group) => group.levelId.toString() === overId);

  if (activeIndex !== -1 && overIndex !== -1) {
    return arrayMove(levelGroups, activeIndex, overIndex);
  }
  return levelGroups;
};

export const shouldHandleDrop = (active: DragEndEvent['active'], over: DragEndEvent['over']) =>
  !!over && active.id !== over.id;

export const isItemToLevelDrop = (active: Active, over: Over | null) => {
  const activeData = active.data.current;
  const overData = over?.data.current;

  return activeData?.type === 'itemType' && overData?.type === 'levelGroup';
};

export const isRowReorder = (active: Active, over: Over | null) => {
  const activeData = active.data.current;
  const overData = over?.data.current;

  return !activeData?.type && !overData?.type;
};

export const shouldUpdateItemTypeLevel = (itemType: ItemType, targetLevelId: number) => {
  return itemType.itemLevelId !== targetLevelId;
};

export const handleItemToLevelDrop = ({
  levelGroups,
  itemType,
  targetLevelId,
}: {
  levelGroups: LevelGroup[];
  itemType: ItemType;
  targetLevelId: number;
}): LevelGroup[] =>
  levelGroups.map((group) => {
    if (group.levelId === itemType.itemLevelId) {
      return {
        ...group,
        items: group.items.filter((item) => item.id !== itemType.id),
      };
    }

    if (group.levelId === targetLevelId) {
      return {
        ...group,
        items: [...group.items, { ...itemType, itemLevelId: targetLevelId }],
      };
    }

    return group;
  });

export const shouldDisableAddHierarchyButton = ({
  itemTypes,
  itemTypeLevels,
}: {
  itemTypes: ItemType[];
  itemTypeLevels: ItemTypeLevel[];
}) =>
  itemTypeLevels.some((level) => !itemTypes.some((itemType) => itemType.itemLevelId === level.id));

export interface LockedSpan {
  minLocked: number;
  maxLocked: number;
}

/**
 * Levels that contain items - and any empty levels trapped between them - form a contiguous block
 * that must keep its relative order. Returns the inclusive `level` range of that block, or null when
 * no level contains items. A type "contains items" when it is not in `deletableItemTypeIds` (the set
 * of types that have no items).
 */
export const computeLockedSpan = (
  itemTypes: ItemType[],
  itemTypeLevels: ItemTypeLevel[],
  deletableItemTypeIds: Set<number> | undefined,
): LockedSpan | null => {
  if (!deletableItemTypeIds) {
    return null;
  }

  const itemBearingLevelIds = new Set(
    itemTypes
      .filter((itemType) => !deletableItemTypeIds.has(itemType.id))
      .map((it) => it.itemLevelId),
  );
  const lockedLevels = itemTypeLevels
    .filter((level) => itemBearingLevelIds.has(level.id))
    .map((level) => level.level);

  if (lockedLevels.length === 0) {
    return null;
  }

  return { minLocked: Math.min(...lockedLevels), maxLocked: Math.max(...lockedLevels) };
};

export const isLevelLocked = (level: number, span: LockedSpan | null): boolean =>
  span !== null && level >= span.minLocked && level <= span.maxLocked;

/**
 * Mirrors the server-side guard: a reorder is rejected when it moves a locked level, or when the
 * moved level would split the locked block. `sortedLevels` must be ordered by `level` ascending.
 */
export const wouldReorderSplitLockedSpan = (
  sortedLevels: { id: number; level: number }[],
  movingLevelId: number,
  targetLevel: number,
  span: LockedSpan | null,
): boolean => {
  if (span === null) {
    return false;
  }

  const moving = sortedLevels.find((level) => level.id === movingLevelId);
  if (!moving) {
    return false;
  }
  if (isLevelLocked(moving.level, span)) {
    return true;
  }

  const without = sortedLevels.filter((level) => level.id !== movingLevelId);
  const insertIndex = Math.min(Math.max(targetLevel - 1, 0), without.length);
  const reordered = [...without.slice(0, insertIndex), moving, ...without.slice(insertIndex)];

  const lockedPositions = reordered
    .map((level, index) => (isLevelLocked(level.level, span) ? index : -1))
    .filter((index) => index >= 0);

  const first = lockedPositions[0];
  const last = lockedPositions[lockedPositions.length - 1];
  if (first === undefined || last === undefined) {
    return false;
  }

  return last - first !== lockedPositions.length - 1;
};
